import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, X, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PersonAvatar } from './PersonAvatar';
import { useConfirmation } from './ConfirmationProvider';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const REMOVE_BTN_SIZE = {
  sm: 'h-4 w-4 -top-0.5 -right-0.5',
  md: 'h-5 w-5 -top-0.5 -right-0.5',
  lg: 'h-5 w-5 -top-1 -right-1',
  xl: 'h-6 w-6 -top-1 -right-1',
};
const REMOVE_ICON_SIZE = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
  lg: 'h-3 w-3',
  xl: 'h-3.5 w-3.5',
};

/**
 * Compact profile-picture uploader.
 * - Click the avatar → file picker → circular crop dialog → upload.
 * - Hover overlay with camera icon hints clickability.
 * - Small × badge on top-right when a picture is set → click → confirm → delete.
 *
 * Props:
 *   picture, firstName, lastName, seed, size — passed to PersonAvatar.
 *   onUpload(blob) — async; parent does the actual API call.
 *   onDelete()     — async; optional. Hides × when omitted.
 *   confirmDelete  — bool, default true.
 *   disabled       — disables interaction.
 */
export const ProfilePictureUploader = ({
  picture,
  firstName,
  lastName,
  seed,
  size = 'xl',
  onUpload,
  onDelete,
  confirmDelete = true,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  const confirm = useConfirmation();

  const [busy, setBusy] = useState(false);
  const [cropDialog, setCropDialog] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);

  const openPicker = () => {
    if (disabled || busy) return;
    fileInputRef.current?.click();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > MAX_FILE_BYTES) { toast.error('Image must be smaller than 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ unit: '%', width: 80, aspect: 1 });
      setCompletedCrop(null);
      setCropDialog(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const cropAndUpload = async () => {
    if (!completedCrop || !imgRef.current) { toast.error('Please select a crop area'); return; }
    setBusy(true);
    try {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX, completedCrop.y * scaleY,
        completedCrop.width * scaleX, completedCrop.height * scaleY,
        0, 0, canvas.width, canvas.height
      );
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      await onUpload(blob);
      setCropDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation(); // don't bubble to the avatar's open-picker click
    if (!onDelete || disabled || busy) return;
    if (confirmDelete) {
      const ok = await confirm({
        title: 'Remove Profile Picture',
        description: 'Are you sure you want to remove this profile picture?',
        confirmText: 'Remove',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to remove picture');
    } finally {
      setBusy(false);
    }
  };

  const removeBtnCls = REMOVE_BTN_SIZE[size] || REMOVE_BTN_SIZE.xl;
  const removeIconCls = REMOVE_ICON_SIZE[size] || REMOVE_ICON_SIZE.xl;

  return (
    <>
      <div
        className={`relative inline-block group ${disabled ? '' : 'cursor-pointer'}`}
        onClick={openPicker}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPicker(); } }}
        aria-label={picture ? 'Change profile picture' : 'Upload profile picture'}
        data-testid="profile-picture-uploader"
      >
        <PersonAvatar firstName={firstName} lastName={lastName} picture={picture} seed={seed} size={size} />

        {/* Hover overlay with camera icon — hint that the avatar is clickable */}
        {!disabled && (
          <div className="absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
            <Camera className="h-5 w-5 text-white" />
          </div>
        )}

        {/* Loading spinner over the avatar while uploading/deleting */}
        {busy && (
          <div className="absolute inset-0 rounded-full bg-background/70 flex items-center justify-center pointer-events-none">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        {/* Hidden file input wired to the avatar click */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFile}
          data-testid="profile-pic-input"
          disabled={disabled || busy}
        />

        {/* Remove × badge — appears top-right only when a picture exists */}
        {picture && onDelete && !disabled && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={busy}
            aria-label="Remove profile picture"
            data-testid="profile-pic-delete-btn"
            className={`absolute ${removeBtnCls} rounded-full bg-destructive hover:bg-destructive/90 ring-2 ring-background flex items-center justify-center text-white shadow-md transition-transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <X className={removeIconCls} strokeWidth={3} />
          </button>
        )}
      </div>

      <Dialog open={cropDialog} onOpenChange={(open) => { if (!busy) setCropDialog(open); }}>
        <DialogContent className="max-w-md" data-testid="profile-crop-dialog">
          <DialogHeader><DialogTitle>Crop Profile Picture</DialogTitle></DialogHeader>
          {cropSrc && (
            <div className="flex justify-center bg-muted/30 rounded-md p-2 max-h-[420px] overflow-auto">
              <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} aspect={1} circularCrop>
                <img ref={imgRef} src={cropSrc} alt="Crop" style={{ maxHeight: '380px' }} />
              </ReactCrop>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCropDialog(false)} disabled={busy}>Cancel</Button>
            <Button onClick={cropAndUpload} disabled={busy} data-testid="profile-crop-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfilePictureUploader;
